import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const SIDEBAR_INLINE_SIZE = '430px';

// 16px spacing token — the gap between the floating card and its region's
// edges. Applied as padding on a transparent wrapper so the page background
// shows through the gap.
const FLOATING_CARD_GAP = '400';

// Copilot-sidebar layer (`Z_INDEX_COPILOT_SIDEBAR`): above the wrapped page
// content, below HubSpot's left nav (one stacking context at 1111) so the nav
// rail and its flyouts/tooltips paint over the assistant. We portal to <body>
// after the nav, so sharing 1111 would let us cover them. Below the container's
// FloatingAlertList (10000).
const OVERLAY_Z_INDEX = 1070;

// Above HubSpot's global chrome. The left nav rail + top toolbar top out around
// z-index 1234, so 1300 (the shared `aboveNav` layer) paints the assistant over
// them for the onboarding takeover, while staying below the nav's own global
// search/modal overlays. Only used for the fullscreen onboarding flow.
const ABOVE_CHROME_Z_INDEX = 1300;

/**
 * How the overlay is positioned.
 * - `region`   — `position:absolute`, fills the shell's positioned parent. Use
 *   when the shell is mounted in a fixed-size region (the default; PR5/admin).
 * - `viewport` — `position:fixed`, pinned to the on-screen box of the shell's
 *   nearest scrollable ancestor. Use when the shell is mounted inside a page
 *   that scrolls at the window/container level (e.g. academy-home-ui inside the
 *   quartz app cell), so the panel is the available screen height and stays put
 *   while the page scrolls, instead of growing with content and scrolling away.
 */

/**
 * Nearest ancestor that is a scroll container (by `overflow-y`), or `null` if
 * none. Detected by overflow rather than current overflowing state, so a short
 * (not-yet-scrolling) page still resolves the container.
 */
const getScrollParent = node => {
  var _node$parentElement;
  let el = (_node$parentElement = node === null || node === void 0 ? void 0 : node.parentElement) !== null && _node$parentElement !== void 0 ? _node$parentElement : null;
  while (el) {
    const {
      overflowY
    } = getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

/**
 * Track the on-screen box a `viewport`-anchored overlay should fill: the shell's
 * scroll container, measured from its top down to the bottom of the viewport.
 *
 * Deriving the height as `innerHeight - top` (rather than the container's own
 * `height`) means a container that grows to content height still yields the
 * available on-screen height, and any top chrome that pushes the container down
 * — an announcement/notification/trial banner stacking above the nav — shrinks
 * it automatically. Banners resize the container as they appear/disappear, so
 * the `ResizeObserver` re-measures; `window` resize covers viewport changes.
 */
const useMeasuredScrollParent = (enabled, sentinelRef) => {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const region = getScrollParent(sentinelRef.current);
    if (!region) {
      return undefined;
    }
    const measure = () => {
      const r = region.getBoundingClientRect();
      const next = {
        top: r.top,
        left: r.left,
        width: r.width,
        height: Math.max(0, window.innerHeight - r.top)
      };
      setRect(prev => prev && prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height ? prev : next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(region);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, sentinelRef]);
  return rect;
};

/**
 * On-top layer hosting the assistant panel. In `region` mode it fills its
 * positioned parent; in `viewport` mode it is fixed to the scroll container's
 * on-screen box (the available screen area). The fullscreen onboarding takeover
 * (`mode === 'fullscreen'` + `isOnboardingFlow`) overrides `anchor` and fills
 * the whole viewport, portaled above HubSpot's nav and header.
 *
 * The outer wrapper pads all sides by 16px so the inner card floats; the inner
 * card carries the surface and rounded corners. In sidebar mode the wrapper
 * stays transparent so the real page shows through the gap (non-blocking,
 * beside the page). In fullscreen mode the wrapper still sits on top of the
 * live page (portaled, in `viewport` anchor mode), so it paints its own grey
 * backdrop instead — otherwise the gap would be a transparent hole exposing
 * whatever page content happens to sit underneath. That grey + the card's
 * radius mirror HomePageLayout's own `View` + `CardWrapper` pairing.
 */
export const Overlay = ({
  mode,
  anchor = 'region',
  isOnboardingFlow = false,
  hideGradient = false,
  children
}) => {
  const isSidebar = mode === 'sidebar';
  const isViewport = anchor === 'viewport';
  // The fullscreen onboarding takeover covers the whole viewport (over the nav
  // and header), overriding `anchor` — so it never measures a scroll parent.
  const isOnboardingTakeover = mode === 'fullscreen' && isOnboardingFlow;
  const sentinelRef = useRef(null);
  const rect = useMeasuredScrollParent(isViewport && !isOnboardingTakeover, sentinelRef);

  // Fixed + portaled whenever the panel must escape its normal flow: `viewport`
  // mode pins to the measured scroll box, and the onboarding takeover covers the
  // viewport above the chrome. We always portal in viewport mode (not only once
  // `rect` arrives) so the subtree is never unmounted/remounted when `rect`
  // toggles a fragment position between an element and a portal — that remount
  // would fire initChat twice. Before `rect` is measured we stretch to the
  // viewport edges (0/0/0/0) as a safe fallback; `useLayoutEffect` delivers the
  // exact box synchronously before the first paint, so the fallback is never
  // actually seen. (The takeover never measures a scroll parent, so its `rect`
  // stays null and it simply fills the viewport via the 0/0/0/0 fallback.)
  const useFixed = isViewport || isOnboardingTakeover;
  const layer = /*#__PURE__*/_jsx(Flex, {
    position: useFixed ? 'fixed' : 'absolute',
    insetBlockStart: useFixed && rect ? `${rect.top}px` : '0px',
    insetBlockEnd: useFixed && rect ? undefined : '0px',
    blockSize: useFixed && rect ? `${rect.height}px` : undefined,
    insetInlineStart: useFixed && rect ? `${rect.left}px` : '0px',
    insetInlineEnd: isSidebar || useFixed && rect !== null ? undefined : '0px',
    inlineSize: isSidebar ? SIDEBAR_INLINE_SIZE : !isOnboardingTakeover && useFixed && rect ? `${rect.width}px` : undefined,
    zIndex: isOnboardingTakeover ? ABOVE_CHROME_Z_INDEX : OVERLAY_Z_INDEX,
    flexDirection: "column",
    backgroundColor: isSidebar ? undefined : 'fill-surface-recessed',
    paddingBlockStart: FLOATING_CARD_GAP,
    paddingBlockEnd: FLOATING_CARD_GAP,
    paddingInlineStart: FLOATING_CARD_GAP,
    paddingInlineEnd: FLOATING_CARD_GAP,
    children: /*#__PURE__*/_jsxs(Flex, {
      position: "relative",
      zIndex: 0,
      flex: 1,
      minBlockSize: "0px",
      flexDirection: "column",
      overflow: "hidden",
      borderWidth: "100",
      borderStyle: "solid",
      borderColor: "fill-accent-light-orange-subtle",
      borderRadius: "400",
      boxShadow: "100",
      backgroundColor: hideGradient ? 'fill-surface-default' : undefined,
      children: [!hideGradient && /*#__PURE__*/_jsx("div", {
        "aria-hidden": true,
        style: {
          position: 'absolute',
          inset: 0,
          zIndex: -1,
          backgroundImage: 'radial-gradient(ellipse 100% 80% at 100% 100%, rgba(252, 230, 222, 0.75) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(#fff, #fff)'
        }
      }), children]
    })
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isViewport && /*#__PURE__*/_jsx("span", {
      "aria-hidden": true,
      ref: sentinelRef,
      style: {
        display: 'none'
      }
    }), useFixed ? /*#__PURE__*/createPortal(layer, document.body) : layer]
  });
};